import { useEffect, useState } from 'react'
import type { TenantBootstrapModel } from '@unified-lk/tenant-config'
import { PublicMisPlanPage } from './PublicMisPlanPage'
import { PublicTreatmentPlanPage } from './PublicTreatmentPlanPage'

export function PublicSharedPlanRouter({
  token,
  bootstrap,
}: {
  token: string
  bootstrap: TenantBootstrapModel
}) {
  const [route, setRoute] = useState<'detecting' | 'constructor' | 'mis'>('detecting')

  useEffect(() => {
    let cancelled = false
    const constructorUrls = [
      `${bootstrap.apiBase}/public/plans/${encodeURIComponent(token)}`,
      `${bootstrap.apiBase}/public/constructor-plans/${encodeURIComponent(token)}`,
    ]

    void (async () => {
      for (const url of constructorUrls) {
        try {
          const response = await fetch(url, { headers: { Accept: 'application/json' } })
          if (response.ok) {
            if (!cancelled) setRoute('constructor')
            return
          }
          if (response.status === 403) {
            const json = await response.json().catch(() => ({})) as { status?: string }
            if (json.status === 'password_required') {
              if (!cancelled) setRoute('constructor')
              return
            }
          }
        } catch {
          // try next constructor endpoint
        }
      }

      try {
        const misResponse = await fetch(
          `${bootstrap.apiBase}/public/mis-plans/${encodeURIComponent(token)}`,
          { headers: { Accept: 'application/json' } },
        )
        if (!cancelled && (misResponse.ok || misResponse.status === 410 || misResponse.status === 403)) {
          setRoute('mis')
          return
        }
      } catch {
        // fall through to constructor error UI
      }

      if (!cancelled) setRoute('constructor')
    })()

    return () => { cancelled = true }
  }, [bootstrap.apiBase, token])

  if (route === 'detecting') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-sky-500" />
      </div>
    )
  }

  if (route === 'mis') {
    return <PublicMisPlanPage bootstrap={bootstrap} publicToken={token} />
  }

  return <PublicTreatmentPlanPage bootstrap={bootstrap} token={token} />
}
